<template>
    <div
        v-show="isLoaded"
        v-gtm:label="combinationGtmLabel"
        v-gtm:@view
        class="card notranslate"
    >
        <div class="card_content">
            <MpCombinationSubBetBlock
                v-for="(subBetKey) in mpCombination.subBetKeys"
                v-bind:key="subBetKey.key"
                v-bind:subBetKey="subBetKey"
            />
        </div>
        <div class="card_footer">
            <span>{{ i18n.t('mp_combination_max_odds') }}:</span>
            <strong class="oddsPrice" v-bind:class="{ 'odds-change': isOddsChanging }">@ {{ totalOddsDisplay }}</strong>
            <VButton
                v-gtm:@click
                v-gtm:label="`${combinationGtmLabel}_Add`"
                color="primary"
                hoverColor="primary"
                textColor="white"
                padding="var(--spacers-md) var(--spacers-xl)"
                v-on:click="addToBetSlip"
            >
                {{ i18n.t('mp_combination_add_to_slip') }}
            </VButton>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import xorBy from 'lodash/xorBy';
    import { storeToRefs } from 'pinia';
    import { computed, ref, watch } from 'vue';
    import VButton from '@/components/common/button/VButton.vue';
    import { MpCombinationContext } from '@/components/mpRecommendation/MpCombinationContext';
    import MpCombinationSubBetBlock from '@/components/mpRecommendation/MpCombinationSubBetBlock.vue';
    import { i18n } from '@/core/lib/i18n';
    import { formatMinDecimalPlace } from '@/core/lib/numberFormat';
    import { getTotalOddsPrice } from '@/core/lib/ticket';
    import type { IMpCombination } from '@/interface/IPopularMixParlay';
    import { AsiaBetPageType, SportType, TabName } from '@/interface/enum';
    import { useAsiaSportMarketInfoStore } from '@/store/asiaSportMarketInfoStore';
    import { useBetSlipStore } from '@/store/betSlipStore';
    import { useLayoutStore } from '@/store/layoutStore';

    const props = defineProps<{
        mpCombination: IMpCombination;
        order: number;
    }>();

    const isOddsChanging = ref(false);

    const { combination, isLoaded } = MpCombinationContext.provide(props.mpCombination);

    const { removeParlayBetSlip, setParlayBetSlipTickets, createParlayTicket } = useBetSlipStore();
    const { parlayBetSlip } = storeToRefs(useBetSlipStore());
    const { setBetSlipActiveTab, toggleIsSportListExpanded, toggleBetSlipExpanded, toggleMyBetsExpanded } = useLayoutStore();
    const { isLayoutExpanded } = storeToRefs(useLayoutStore());

    const tickets = computed(() => Array.from(combination.value.values()).map((ticket) => {
        if (ticket.event === null || ticket.odds === null || ticket.priceOption === null) {
            return null;
        }
        return createParlayTicket(ticket.event, ticket.odds, ticket.priceOption, AsiaBetPageType.MpRecommendation);
    }).filter(x => x !== null));

    const totalOdds = computed(() => getTotalOddsPrice(tickets.value));
    const totalOddsDisplay = computed(() => formatMinDecimalPlace(totalOdds.value, 2));

    const { sportType } = storeToRefs(useAsiaSportMarketInfoStore());
    const combinationGtmLabel = computed(() => `${SportType[sportType.value]}_${props.order}`);

    function addToBetSlip() {
        const difference = xorBy(parlayBetSlip.value?.tickets ?? [], tickets.value, x => x.key);
        if (difference.length > 0) {
            removeParlayBetSlip();
            setParlayBetSlipTickets(tickets.value);
        }

        setBetSlipActiveTab(TabName.Parlay);
        toggleIsSportListExpanded(false);
        toggleBetSlipExpanded(true);
        if (!isLayoutExpanded.value) toggleMyBetsExpanded(false);
    }

    watch(totalOdds, () => {
        isOddsChanging.value = true;
        setTimeout(() => {
            isOddsChanging.value = false;
        }, 1000);
    });
</script>

<style lang="scss" scoped>
@import "@/components/mpRecommendation/MpCombination";
</style>
